import { z } from '@customerio/hydra-validation';
import type {
  Delivery as UIAPIDelivery,
  DeliveryDetail as UIAPIDeliveryDetail,
  operations,
} from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Delivery entity — the Outbox delivery log. Reused by campaign / broadcast /
 * newsletter / transactional / person delivery lists.
 *
 * The wire model carries every state timestamp as a nullable unix-second int
 * (`sent`, `delivered`, `drafted`, …) plus a server `state` enum. We derive
 * the display state from the timestamps (see `utils.ts`) to match the Ember
 * model exactly — the server `state` enum is NOT equivalent (it lacks
 * `holdout`/`canceled`, has `dropped`). Timestamps are left as raw numbers.
 *
 * `pendingSend` / `pendingRetry` / `pendingDelete` are client-only optimistic
 * flags (not on the wire) — the Ember model's `@tracked` flags, folded onto
 * the record. Bulk-op mutations set them in place instead of refetching.
 */
export type Delivery = UIAPIDelivery & {
  pendingSend?: boolean;
  pendingRetry?: boolean;
  pendingDelete?: boolean;
};

export type DeliveryDetail = UIAPIDeliveryDetail;

const DeliveryFromApi = readSchema<Delivery>(
  z.looseObject({
    id: z.string(),
    action_type: z.string(),
  }),
);

/** @public */
export const delivery = defineEntity<Delivery, Delivery, DeliveriesListParams, undefined, 'workspace'>({
  name: 'delivery',
  scope: 'workspace',
  pk: (d) => d.id,
  schema: { read: DeliveryFromApi },
});

/**
 * The list filter params (mirrors the Ember `getDeliveryQuery` output + the
 * generated list query params). `size` and `continuation` are intentionally
 * excluded: pagination owns them, so they must not fragment the list query key.
 */
type DeliveriesListQuery = NonNullable<
  operations['get-v1-environments-environment_id-deliveries']['parameters']['query']
>;

export type DeliveryType = NonNullable<DeliveriesListQuery['type']>;

export type DeliveriesListParams = Omit<DeliveriesListQuery, 'size' | 'continuation'>;

export const deliveryKeys = {
  ...delivery.keys,
  filters: (workspaceId: number) => [...delivery.keys.all(workspaceId), 'filters'] as const,
  /** The background check for deliveries newer than the list shows. Its own key, so it never changes the list. */
  newCheck: (workspaceId: number, params: DeliveriesListParams) =>
    [...delivery.keys.all(workspaceId), 'new-check', params] as const,
  detailContent: (workspaceId: number, id: string) => [...delivery.keys.detail(workspaceId, id), 'content'] as const,
  detailEdit: (workspaceId: number, id: string) => [...delivery.keys.detail(workspaceId, id), 'edit'] as const,
};

export function parseDelivery(raw: UIAPIDelivery): Delivery {
  return delivery.parse(raw);
}
